import type { JSX, PropsWithChildren} from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import gtag from '../analytics/gtag';
import { ConfirmEmailModal, LoginNeededModal } from '../generic/Modals';
import type { User } from '../model/types';
import { login, logout, onUserChange, refreshUser } from '../utils/auth';
import useComponentDidMount from '../utils/useComponentDidMount';

interface UserContextFunctions {
    login(username: string, password: string): Promise<void>;
    refreshUser(): Promise<void>;
    logout(): Promise<void>;
}

interface PendingUser extends UserContextFunctions {
    isLoggedIn: undefined;
    user: undefined;
    showLoginModal(): undefined;
}

export interface LoggedInUser extends UserContextFunctions {
    isLoggedIn: true;
    user: Readonly<User>;
    showLoginModal(): false;
}

export interface AnonymousUser extends UserContextFunctions {
    isLoggedIn: false;
    user: null;
    showLoginModal(): true;
}

export type UserContext = Readonly<LoggedInUser | AnonymousUser | PendingUser>;

const defaultUserContext: UserContext = {
    isLoggedIn: undefined,
    user: undefined,
    showLoginModal() {
        return undefined;
    },
    refreshUser: () => Promise.resolve(),
    login: () => Promise.resolve(),
    logout: () => Promise.resolve(),
};

const UserContext = createContext<UserContext>(defaultUserContext);

export default function useUser(): UserContext {
    return useContext(UserContext);
}

export function UserContextProvider({ children }: PropsWithChildren): JSX.Element {
    const userContext: UserContext = getUserContext();

    useEffect(() => {
        if (userContext.user?.requiresEmailVerification) {
            ConfirmEmailModal.upsert();
        }
    }, [userContext.user]);

    return <UserContext value={userContext}>{children}</UserContext>;
}

const anonymousUser: AnonymousUser = {
    isLoggedIn: false,
    user: null,
    login,
    logout,
    refreshUser,
    showLoginModal() {
        gtag('event', 'modal_view', {
            modal_name: 'LoginNeeded',
        });
        LoginNeededModal.upsert();
        return true;
    },
};

const pendingUser: PendingUser = {
    isLoggedIn: undefined,
    user: undefined,
    login,
    logout,
    refreshUser,
    showLoginModal() {
        return undefined;
    },
};

function getUserContext(): UserContext {
    const user = getUser();

    switch (user) {
        case null:
            return anonymousUser;
        case undefined:
            return pendingUser;
        default:
            return {
                isLoggedIn: true,
                user,
                login,
                logout,
                refreshUser,
                showLoginModal() {
                    return false;
                },
            };
    }
}

function getUser() {
    const [user, setUser] = useState<User | null | undefined>(undefined);

    useComponentDidMount(() => onUserChange(setUser));

    return user;
}
